import type { SignInSurface } from "./events";
import { track } from "./telemetry";

/**
 * Sign-in funnel bookkeeping: which surface a sign-in started from, and
 * whether a Firebase auth snapshot is a real sign-in or just a restored
 * session.
 *
 * The surface is latched in `sessionStorage` so it survives the
 * `signInWithRedirect` round-trip; `telemetryAuthBridge` consumes it when
 * the user arrives and stamps it onto `sign_up` / `login`.
 */

const PENDING_KEY = "room-canvas:signin:pending";

/** A pending latch older than this is treated as abandoned. */
export const PENDING_SIGN_IN_TTL_MS = 10 * 60 * 1000;

/** A restored session counts as a redirect sign-in only if Firebase
 *  recorded the sign-in this recently. */
export const RECENT_SIGN_IN_MS = 5 * 60 * 1000;

/** Creation and last-sign-in timestamps this close mean a new account. */
const NEW_ACCOUNT_SKEW_MS = 10 * 1000;

interface PendingSignIn {
  surface: SignInSurface;
  at: number;
}

let memoryPending: PendingSignIn | null = null;

export function markSignInStarted(surface: SignInSurface, now = Date.now()): void {
  const pending: PendingSignIn = { surface, at: now };
  memoryPending = pending;
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // private mode: the in-memory copy still covers popup sign-ins
  }
  track("sign_in_started", { surface });
}

export function markSignInFailed(surface: SignInSurface, kind: string): void {
  clearPending();
  track("sign_in_failed", { surface, kind });
}

/** Read and clear the pending sign-in, if it is still fresh. */
export function consumePendingSignIn(now = Date.now()): PendingSignIn | null {
  let pending = memoryPending;
  if (!pending) {
    try {
      const raw = window.sessionStorage.getItem(PENDING_KEY);
      pending = raw ? (JSON.parse(raw) as PendingSignIn) : null;
    } catch {
      pending = null;
    }
  }
  clearPending();
  if (!pending || typeof pending.at !== "number") return null;
  if (now - pending.at > PENDING_SIGN_IN_TTL_MS) return null;
  return pending;
}

/** Peek without clearing. Used to recognise a redirect return. */
export function hasPendingSignIn(now = Date.now()): boolean {
  if (memoryPending) return now - memoryPending.at <= PENDING_SIGN_IN_TTL_MS;
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return false;
    const p = JSON.parse(raw) as PendingSignIn;
    return typeof p.at === "number" && now - p.at <= PENDING_SIGN_IN_TTL_MS;
  } catch {
    return false;
  }
}

function clearPending(): void {
  memoryPending = null;
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

export interface AuthMetadata {
  creationTime?: string | null;
  lastSignInTime?: string | null;
}

/** Firebase stamps both timestamps from the same request on account creation. */
export function isNewAccount(meta: AuthMetadata | null | undefined): boolean {
  const created = Date.parse(meta?.creationTime ?? "");
  const last = Date.parse(meta?.lastSignInTime ?? "");
  if (!Number.isFinite(created) || !Number.isFinite(last)) return false;
  return Math.abs(last - created) <= NEW_ACCOUNT_SKEW_MS;
}

export type AuthTransition = "sign_in" | "restore" | "none";

/**
 * Classify a signed-in auth snapshot.
 *
 * - `sign_in`: the previous snapshot was signed out, or this is the first
 *   snapshot of the page but a sign-in was pending and Firebase recorded
 *   it just now (the `signInWithRedirect` return).
 * - `restore`: first snapshot of the page, user already signed in.
 * - `none`: the user was already signed in (duplicate snapshot).
 */
export function classifyAuthTransition(input: {
  firstSnapshot: boolean;
  wasSignedIn: boolean;
  pendingSignIn: boolean;
  lastSignInTime: string | null | undefined;
  now: number;
}): AuthTransition {
  if (input.wasSignedIn) return "none";
  if (!input.firstSnapshot) return "sign_in";
  if (!input.pendingSignIn) return "restore";
  const last = Date.parse(input.lastSignInTime ?? "");
  if (Number.isFinite(last) && input.now - last <= RECENT_SIGN_IN_MS) return "sign_in";
  return "restore";
}

/** Test-only. */
export function _resetSignInFunnelForTests(): void {
  clearPending();
}
